import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useParams } from "react-router";

import { ApiHelper as ToolkitApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import { CHART_TYPE_HORIZONTAL_STACKED_BAR, ChartBase } from "@datanac/datanac-visualization-toolkit";
import { AddCircleOutline } from "@mui/icons-material";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { _sessionManager, RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { CropProfileEditDialog } from "components/Broker/CropProfileEditDialog";
import { getUnitOfMeasureAbbreviation } from "components/Budget/BudgetService";
import { BUDGET_STATUS_ACTIVE, getBudgetYears } from "components/Budget/helpers";
import EmptyCard from "components/Cards/EmptyCard";
import DataFormSelect from "components/Core/DataFormSelect";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import OTCDescriptionDialog from "components/Marketing/OTC/OTCDescriptionDialog";
import { getUniqueCropPractices } from "components/Vault/Farm/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import _ from "lodash";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { useSnackbar } from "notistack";
import {
  formatDateContext,
  formatDateWithoutYear,
  formatNumber,
  formatPrice,
  formatProducerName,
} from "utility/formatters";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { DeleteIcon, MarketingIcon } from "../Icons/Icons";
import { PrintableTilleyDataGrid } from "../shared/DataGrid/PrintableTilleyDataGrid";
import { CashSummaryTable } from "./CashSummaryTable";
import { FuturesOptionsEditDialog } from "./FuturesOptionsEditDialog";
import FuturesOptionsPanels, { FuturesOptionsExportButton, FuturesOptionsPnLCard } from "./FuturesOptionsPanels";
import { ProductionSummaryTable } from "./ProductionSummaryTable";
import { SalesEditDialog } from "./SalesEditDialog";
import {
  formatDeliveryPeriod,
  SALE_CATEGORY_BASIS,
  SALE_CATEGORY_CASH,
  SALE_CATEGORY_HTA,
  SALE_CATEGORY_NO_PRICE,
} from "./SalesHelper";
import { useRevenueReport } from "./useRevenueReport";

const ACCORDION_STORAGE_KEY = "positionSummary.accordionExpanded";
const DEFAULT_ACCORDION_EXPANDED = {
  production: false,
  positionSummary: true,
  cashSummary: true,
  cashSales: false,
  positions: false,
};

function loadAccordionExpanded() {
  try {
    const raw = localStorage.getItem(ACCORDION_STORAGE_KEY);
    return raw ? { ...DEFAULT_ACCORDION_EXPANDED, ...JSON.parse(raw) } : { ...DEFAULT_ACCORDION_EXPANDED };
  } catch {
    return { ...DEFAULT_ACCORDION_EXPANDED };
  }
}

export default function PositionSummaryReportPane() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const { producer_token, defaultYear, default_commodity_name } = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const isMobileTable = useMediaQuery(theme.breakpoints.down("sm"));

  const [cashSummaryAllExpanded, setCashSummaryAllExpanded] = useState({});
  const [accordionExpanded, setAccordionExpanded] = useState(loadAccordionExpanded);

  const setAccordionSection = useCallback((key, expanded) => {
    setAccordionExpanded((prev) => {
      const next = { ...prev, [key]: expanded };
      try {
        localStorage.setItem(ACCORDION_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  }, []);

  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();
  const { addAttachments } = useChatAssistantAttachments();
  // ---

  const [isLoadingCurrent, setIsLoadingCurrent] = useState(false);

  const [currentYear, setCurrentYear] = useState(defaultYear ?? globalState?.crop_year ?? getCurrentReinsuranceYear());
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  // --- --- ---

  const [positionSummaryData, setPositionSummaryData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const _loadPositionSummaryData = async () => {
    try {
      if (globalState?.agent_token && !globalState?.producer_token) {
        setIsLoading(true);

        const _positionSummaryFilter = {
          agent_token: globalState?.agent_token,
          year: currentYear,
          producer_token: producer_token || globalState?.producer_token,
        };
        // This is agent function. Do not specify producer_token or you will get permissions error.
        const _apiConfig = {
          agent_token: globalState?.agent_token,
        };
        const _positionSummaryData = await UsersApiHelper.users_search(
          "PositionSummary",
          _positionSummaryFilter,
          _apiConfig
        );
        let _filteredPositionSummaryData = _positionSummaryData;
        if (filter?.commodity_name?.length) {
          _filteredPositionSummaryData = _positionSummaryData.filter((obj) =>
            filter?.commodity_name?.includes(obj.commodity_name)
          );
        }

        setPositionSummaryData(_filteredPositionSummaryData);
        setIsLoading(false);
      } else {
        setIsLoading(true);

        const _positionSummaryFilter = {
          year: currentYear,
          producer_token: producer_token ? producer_token : globalState?.producer_token,
        };
        // This is ADMIN function. Do not specify producer_token or agent_token or you will get permissions error.
        const _apiConfig = {};
        const _positionSummaryData = await UsersApiHelper.users_search(
          "PositionSummary",
          _positionSummaryFilter,
          _apiConfig
        );
        let _filteredPositionSummaryData = _positionSummaryData;
        if (filter?.commodity_name?.length) {
          _filteredPositionSummaryData = _positionSummaryData.filter((obj) =>
            filter?.commodity_name?.includes(obj.commodity_name)
          );
        }

        setPositionSummaryData(_filteredPositionSummaryData);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      setPositionSummaryData(null);
      handleError(err);
    }
  };

  // ---

  const [salesByCategory, setSalesByCategory] = useState(null);
  const [sales, setSales] = useState(null);
  const [isLoadingSalesByCategory, setIsLoadingSalesByCategory] = useState(false);
  const _loadSalesByCategory = async () => {
    try {
      if (globalState?.agent_token && !globalState?.producer_token) {
        setIsLoading(true);

        const _positionSummaryDataFilter = {
          agent_token: globalState?.agent_token,
          year: currentYear,
          producer_token: producer_token || globalState?.producer_token,
        };
        // This is agent function. Do not specify producer_token or you will get permissions error.
        const _apiConfig = {
          agent_token: globalState?.agent_token,
        };
        const _positionSummaryData = await UsersApiHelper.users_search(
          "PositionSummarySalesByCategory",
          _positionSummaryDataFilter,
          _apiConfig
        );
        let _filteredPositionSummaryData = _positionSummaryData;
        if (filter?.commodity_name?.length) {
          _filteredPositionSummaryData = _positionSummaryData.filter((obj) =>
            filter?.commodity_name?.includes(obj.commodity_name)
          );
        }

        setSalesByCategory(_filteredPositionSummaryData);
        setIsLoadingSalesByCategory(false);
      } else {
        setIsLoadingSalesByCategory(true);

        const _positionSummaryDataFilter = {
          year: currentYear,
          producer_token: producer_token ? producer_token : globalState?.producer_token,
        };
        // This is ADMIN function. Do not specify producer_token or agent_token or you will get permissions error.
        const _apiConfig = {};
        const _positionSummaryData = await UsersApiHelper.users_search(
          "PositionSummarySalesByCategory",
          _positionSummaryDataFilter,
          _apiConfig
        );
        let _filteredPositionSummaryData = _positionSummaryData;
        if (filter?.commodity_name?.length) {
          _filteredPositionSummaryData = _positionSummaryData.filter((obj) =>
            filter?.commodity_name?.includes(obj.commodity_name)
          );
        }

        setSalesByCategory(_filteredPositionSummaryData);
        setIsLoadingSalesByCategory(false);
      }
    } catch (err) {
      setIsLoadingSalesByCategory(false);
      setSalesByCategory(null);
      handleError(err);
    }
  };

  const getSalesRowsByCommodity = useCallback(
    (commodity_name) => {
      if (sales?.length) {
        return sales.filter((s) => s.commodity_name == commodity_name);
      } else {
        return [];
      }
    },
    [sales]
  );

  const _loadSales = async () => {
    try {
      setIsLoadingCurrent(true);
      // Get Sales:
      const _salesFilter = {
        crop_year: currentYear,
        producer_token: producer_token ? producer_token : globalState?.producer_token,
        is_active: true,
      };
      const _sales = await UsersApiHelper.users_search("Sale", _salesFilter);
      let _filteredSales = _sales;

      // Add dummy fields:
      _filteredSales = _filteredSales.map((s) => {
        if (s?.category == SALE_CATEGORY_CASH || s?.category == SALE_CATEGORY_HTA) {
          s.price_cash = (s?.price || 0) + (s?.basis || 0);
        } else {
          s.price_cash = null;
        }
        return s;
      });

      setSales(_filteredSales);
      setIsLoading(false);
      setIsLoadingCurrent(false);
    } catch (err) {
      setSales(null);
      setIsLoading(false);
      setIsLoadingCurrent(false);
      handleError(err);
    }
  };

  const handleRowDelete_Sales = useCallback(
    async (row) => {
      confirmDialogActions.confirm({
        title: "Delete this entry?",
        prompt: "Are you sure you want to delete this entry?",
        promptDetail: null,
        callback: () => {
          _handleRowDeleteIntern_Sales(row);
        },
      });
    },
    [confirmDialogActions]
  );

  const _handleRowDeleteIntern_Sales = async (row) => {
    const _row = { ...row };
    _row.is_active = false;

    updateSale(_row);
    _loadSales();
  };

  const sales_columns = useMemo(
    () => [
      {
        field: "trade_date",
        headerName: "Trade Date",
        type: "date",
        // minWidth: 100,
        valueFormatter: (value) => formatDateWithoutYear(value),
        // flex: 1,
        width: 80,
      },
      {
        field: "financial_institution_name",
        headerName: "Buyer",
        minWidth: 120,
        flex: 1,
        cellClassName: "wrap-text",
        // width: 150,
      },
      {
        field: "commodity_name",
        headerName: "Commodity",
        minWidth: 100,
        flex: 1,
        cellClassName: "no-print",
        headerClassName: "no-print",
        // width: 80,
      },
      {
        field: "category",
        headerName: "Category",
        // minWidth: 100,
        // flex: 1,
        width: 80,
      },
      {
        field: "symbol",
        headerName: "Symbol",
        // minWidth: 0,
        // flex: 0,
        width: 90,
      },
      {
        field: "price",
        headerName: "Futures",
        minWidth: 80,
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value !== null && value !== undefined && value !== "" ? formatNumber(value, 4) : ""),
      },
      {
        field: "basis",
        headerName: "Basis",
        type: "number",
        minWidth: 80,
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value !== null && value !== undefined && value !== "" ? formatNumber(value, 4) : ""),
      },
      {
        field: "price_cash",
        headerName: "Cash",
        minWidth: 80,
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value !== null && value !== undefined && value !== "" ? formatNumber(value, 4) : ""),
      },
      {
        field: "amount",
        headerName: "Quantity",
        type: "number",
        // minWidth: 80,
        valueFormatter: (value) => formatNumber(value, 0),
        align: "right",
        headerAlign: "right",
        // flex: 1,
        width: 80,
      },
      {
        field: "delivery_period_start",
        headerName: "Delivery",
        // minWidth: 100,
        // flex: 1,
        width: 80,
        valueFormatter: (value, row) =>
          sales?.length ? formatDeliveryPeriod(sales?.find((s) => s.id === row?.id)) : null,
      },
      {
        field: "contract_number",
        headerName: "Contract",
        // minWidth: 80,
        flex: 1,
        // width: 120,
      },
      {
        field: "revenue",
        headerName: "Revenue",
        minWidth: 90,
        flex: 1,
        renderCell: (params) => (
          <Box>
            <abbr title={formatDateContext(params?.row?.market_value_timestamp)}>
              {formatPrice(params.row?.revenue, 0)}
            </abbr>
          </Box>
        ),
        align: "right",
        headerAlign: "right",
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        // minWidth: 100,
        // flex: 1,
        width: 100,
        headerAlign: "right",
        align: "right",
        sortable: false,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            data-testid={`edit-icon-${params?.row?.contract_number}`}
            icon={<EastOutlinedIcon />}
            label="Edit"
            onClick={() => setEditingSale(params?.row)}
          />,
          <GridActionsCellItem
            data-testid={`delete-icon-${params?.row?.contract_number}`}
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleRowDelete_Sales(params?.row)}
          />,
        ],
        cellClassName: "no-print",
        headerClassName: "no-print",
      },
    ],
    [sales]
  );

  const [isLoadingTransactionHistory, setIsLoadingTransactionHistory] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState(null);
  const _loadTransactionHistory = async () => {
    setIsLoadingTransactionHistory(true);
    try {
      if (globalState?.agent_token && !globalState?.producer_token) {
        setIsLoading(true);

        const _transactionHistoryFilter = {
          agent_token: globalState?.agent_token,
          year: currentYear,
          producer_token: producer_token || globalState?.producer_token,
          is_posted: true,
        };
        // This is agent function. Do not specify producer_token or you will get permissions error.
        const _apiConfig = {
          agent_token: globalState?.agent_token,
        };
        const transactionHistory = await UsersApiHelper.users_search(
          "UserTransactionHistory",
          _transactionHistoryFilter,
          _apiConfig
        );

        let _filteredTransactions = transactionHistory;
        // if (filter?.commodity_name?.length) {
        //     _filteredTransactions = transactionHistory
        //         .filter(obj => filter?.commodity_name?.includes(obj.commodity_name));
        // }

        // Order by trade_date ASC
        _filteredTransactions = _filteredTransactions.sort((a, b) => new Date(a.trade_date) - new Date(b.trade_date));

        setTransactionHistory(_filteredTransactions);
        setIsLoading(false);
        setIsLoadingTransactionHistory(false);
      } else {
        setIsLoading(true);

        const _transactionHistoryFilter = {
          year: currentYear,
          producer_token: producer_token ? producer_token : globalState?.producer_token,
          is_posted: true,
        };
        // This is ADMIN function. Do not specify producer_token or agent_token or you will get permissions error.
        const _apiConfig = {};
        const transactionHistory = await UsersApiHelper.users_search(
          "UserTransactionHistory",
          _transactionHistoryFilter,
          _apiConfig
        );

        let _filteredTransactions = transactionHistory;

        if (filter?.commodity_name_list?.length) {
          _filteredTransactions = transactionHistory.filter((obj) =>
            filter?.commodity_name_list?.includes(obj.commodity_name)
          );
        }

        if (filter?.search_contract_number) {
          _filteredTransactions = _filteredTransactions.filter((obj) =>
            obj.contract_number?.toLowerCase().includes(filter?.search_contract_number?.toLowerCase())
          );
        }

        // Order by trade_date ASC
        _filteredTransactions = _filteredTransactions.sort((a, b) => new Date(a.trade_date) - new Date(b.trade_date));
        setTransactionHistory(_filteredTransactions);
        setIsLoading(false);
        setIsLoadingTransactionHistory(false);
      }
    } catch (err) {
      // setIsLoadingTransactionHistory(false);
      // setTransactionHistory(null);
      setIsLoadingTransactionHistory(false);
      console.warn(err);
      enqueueSnackbar(err?.detail || err?.message || err, { preventDuplicate: true, variant: "error" });
    }
  };

  // --- --- ---

  const getTransactionHistoryRowsByCommodity = useCallback(
    (commodity_name) => {
      if (transactionHistory?.length) {
        return transactionHistory
          ?.filter((s) => s.is_active === true)
          ?.filter((s) => s.crop_profile_commodity_name == commodity_name && s.offset_match_id == null);
      } else {
        return [];
      }
    },
    [transactionHistory]
  );

  // ---

  const getTransactionHistoryTotal_Revenue = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }
      const total = getTransactionHistoryRowsByCommodity(commodity_name)
        ?.filter((x) => !x.is_otc)
        .reduce((total, row) => {
          const mainValue = row.is_open ? row.market_value_open || 0 : row.market_value_closed || 0;
          const matchedValues = getTransactionHistoryRowsByMatch(row.id).reduce(
            (matchTotal, matchRow) => matchTotal + (matchRow.market_value_closed || 0),
            0
          );

          return total + mainValue + matchedValues;
        }, 0);
      return total;
    },
    [transactionHistory]
  );

  const calculateFuturesOptionsHedgedTotal = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }

      const transactions = getTransactionHistoryRowsByCommodity(commodity_name);
      if (transactions?.length) {
        // Reduce hedge_quantity * multiplication_factor
        return transactions.reduce((acc, curr) => {
          if (!curr?.is_otc) {
            return acc + curr.hedge_quantity * curr.multiplication_factor;
          } else {
            return acc;
          }
        }, 0);
      } else {
        return 0;
      }
    },
    [transactionHistory, currentYear, getTransactionHistoryRowsByCommodity]
  );

  // ---

  const calculateOTCHedgedTotal = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }

      const transactions = getTransactionHistoryRowsByCommodity(commodity_name);
      if (transactions?.length) {
        return transactions.reduce((acc, curr) => {
          if (curr.is_otc) {
            return acc + curr.quantity_open * curr.multiplication_factor;
          }
          return acc;
        }, 0);
      } else {
        return 0;
      }
    },
    [transactionHistory]
  );

  const getTransactionHistoryOTCHedgedTotal_Revenue = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }

      const transactions = getTransactionHistoryRowsByCommodity(commodity_name);
      if (transactions?.length) {
        return transactions.reduce((acc, curr) => {
          return acc + (curr.market_value_open || curr.market_value_closed);
        }, 0);
      } else {
        return 0;
      }
    },
    [transactionHistory]
  );

  const calculateOTCAccumulatedTotal = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }

      const transactions = getTransactionHistoryRowsByCommodity(commodity_name);
      if (transactions?.length) {
        return transactions.reduce((acc, curr) => {
          return acc + curr.quantity_accumulated * curr.multiplication_factor;
        }, 0);
      } else {
        return 0;
      }
    },
    [transactionHistory]
  );

  const getTransactionHistoryOTCAccumulatedTotal_Revenue = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }

      const transactions = getTransactionHistoryRowsByCommodity(commodity_name);
      if (transactions?.length) {
        return transactions.reduce((acc, curr) => {
          if (curr.is_otc) {
            return acc + curr.market_value_closed;
          }
          return acc;
        }, 0);
      } else {
        return 0;
      }
    },
    [transactionHistory]
  );

  const getOTCAccumulatedAveragePrice = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }

      const transactions = getTransactionHistoryRowsByCommodity(commodity_name);
      if (transactions?.length) {
        const otcTransactions = transactions.filter((curr) => curr.is_otc);
        if (otcTransactions.length) {
          const totalPrice = otcTransactions.reduce((acc, curr) => {
            return acc + (curr.accumulated_average_price || 0);
          }, 0);
          return totalPrice / otcTransactions.length;
        }
      }
      return 0;
    },
    [transactionHistory]
  );

  const getOTCAccumulatedMarketValue = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }

      const transactions = getTransactionHistoryRowsByCommodity(commodity_name);
      if (transactions?.length) {
        return transactions.reduce((acc, curr) => {
          if (curr.is_otc) {
            return acc + (curr.accumulated_market_value || 0);
          }
          return acc;
        }, 0);
      } else {
        return 0;
      }
    },
    [transactionHistory]
  );

  const getTotalRevenueIncludingOTCAccumulated = useCallback(
    (commodity_name) => {
      const baseRevenue = getTransactionHistoryTotal_Revenue(commodity_name);
      const otcAccumulatedRevenue = getOTCAccumulatedMarketValue(commodity_name);
      return baseRevenue + otcAccumulatedRevenue;
    },
    [getTransactionHistoryTotal_Revenue, getOTCAccumulatedMarketValue]
  );

  // ---

  const getTransactionHistoryRowsByMatch = useCallback(
    (id) => {
      if (transactionHistory?.length) {
        return transactionHistory.filter((s) => s.offset_match_id == id);
      } else {
        return [];
      }
    },
    [transactionHistory]
  );

  // ---

  const hasOTC = useCallback(() => {
    if (transactionHistory?.length) {
      return transactionHistory.some((transaction) => transaction.is_otc);
    }

    return false;
  }, [transactionHistory]);

  // ---

  const formatDefaultCropProfile = (row) => {
    return formatCropProfile({
      year: row?.crop_profile_year,
      commodity_name: row?.crop_profile_commodity_name,
      name: row?.crop_profile_name,
    });
  };

  const formatCropProfile = (cp) => {
    if (cp?.year && cp?.commodity_name) {
      if (!cp?.name || cp?.name == cp?.commodity_name) {
        return cp?.year + " - " + cp?.commodity_name;
      } else {
        return cp?.year + " - " + cp?.commodity_name + " - " + cp?.name;
      }
    } else {
      return null;
    }
  };

  const updateTransactionHistory = async (row) => {
    try {
      const api = new ToolkitApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });

      await api.callPost("marketing/UserTransactionHistory", row, globalState);
      await _loadTransactionHistory();
    } catch (err) {
      handleError(err);
    }
  };

  // ---

  const reportData = useMemo(() => {
    if (positionSummaryData?.length && !salesByCategory?.length) {
      return positionSummaryData;
    } else if (positionSummaryData?.length && salesByCategory?.length) {
      const _reportData = [...positionSummaryData];
      for (const ps of _reportData) {
        const _salesByCategory = salesByCategory.filter((sbc) => sbc.commodity_name == ps.commodity_name);
        ps.salesByCategory = _salesByCategory || [];

        for (const currentSaleByCategory of ps.salesByCategory) {
          // Calculate percentage of production
          if (ps.production) {
            currentSaleByCategory.percentage_of_production = currentSaleByCategory.amount / ps.production;
          } else {
            currentSaleByCategory.percentage_of_production = null;
          }
        }
      }
      return _reportData;
    } else {
      return null;
    }
  }, [positionSummaryData, salesByCategory]);

  // ---

  const [cropProfiles, setCropProfiles] = useState([]);
  const [isLoadingCropProfiles, setIsLoadingCropProfiles] = useState(false);

  const calculateMarketingTotal = useCallback(
    (commodity_name) => {
      if (!commodity_name) {
        return 0;
      }

      const currentCountyPractice = reportData?.find((r) => r.commodity_name == commodity_name);
      // Loop salesByCategory sum ps?.amount * (ps?.price_per_unit || currentCountyPractice?.market_last_price)
      const cpBasis = Number(cropProfiles?.find(
        (cp) =>
          cp.commodity_name?.trim().toLowerCase() === commodity_name?.trim().toLowerCase() &&
          String(cp.year) === String(currentYear)
      )?.basis) || 0;
      const unsoldPrice = (currentCountyPractice?.market_last_price || 0) + cpBasis;

      if (currentCountyPractice?.salesByCategory?.length) {
        let totalQty = 0;
        const salesTotal = currentCountyPractice.salesByCategory.reduce((acc, curr) => {
          // If "No Price Established", then use market_last_price.
          totalQty += curr?.amount;
          if (curr?.category === SALE_CATEGORY_NO_PRICE) {
            return acc + curr?.amount * currentCountyPractice?.market_last_price;
          } else {
            return acc + curr?.revenue;
          }
        }, 0);

        const unsoldTotal = (currentCountyPractice?.production - totalQty) * unsoldPrice;

        return salesTotal + unsoldTotal;
      } else {
        const unsoldTotal = currentCountyPractice?.production * unsoldPrice;
        return unsoldTotal;
      }
    },
    [reportData, transactionHistory, cropProfiles, currentYear]
  );

  // Total sales + unsold toal + Futures/Options Hedged
  const calculateFinalMarketingTotal = useCallback((marketing_total = 0, hedged_total = 0) => {
    return (Number(marketing_total) || 0) + (Number(hedged_total) || 0);
  }, []);

  /// --- --- ---

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const revenueReportProducerState = useMemo(
    () => ({ ...globalState, producer_token: producer_token || globalState?.producer_token }),
    [globalState, producer_token]
  );

  // Use revenue report hook for ARC/PLC data
  const {
    getArcPlcPaymentForCrop,
    getCropInsurancePaymentForCrop,
    isLoadingData: isLoadingRevenueData,
  } = useRevenueReport({
    onError: handleError,
    currentYear,
    producerState: revenueReportProducerState,
  });

  const [budgetCountyPractices, setBudgetCountyPractices] = useState(null);

  // Helper function to calculate total reported acreage for a specific commodity
  const calculateTotalAcresForCrop = useCallback(
    (commodityName) => {
      if (!budgetCountyPractices?.length || !commodityName) return 0;

      return _.sumBy(
        budgetCountyPractices.filter((bcp) => bcp.commodity_name === commodityName),
        "reported_acreage"
      );
    },
    [budgetCountyPractices]
  );

  // Helper function to calculate total production cost for a specific commodity
  const calculateTotalCostForCrop = useCallback(
    (commodityName) => {
      if (!budgetCountyPractices?.length || !commodityName) return 0;

      return _.sumBy(
        budgetCountyPractices.filter((bcp) => bcp.commodity_name === commodityName),
        "production_cost"
      );
    },
    [budgetCountyPractices]
  );

  const loadBudgetCountyPractices = async () => {
    try {
      const _budgetFilter = {
        // state_name, county_name,
        // commodity_name, type_name, practice_name,
        year: currentYear,
        status: BUDGET_STATUS_ACTIVE,
        is_active: true,
        producer_token: producer_token || globalState?.producer_token,
      };
      // if (commodity_name) { _budgetFilter.commodity_name = commodity_name; }
      // if (type_name) { _budgetFilter.type_name = type_name; }
      // if (practice_name) { _budgetFilter.practice_name = practice_name; }

      const _allCountyPractices = await UsersApiHelper.selectBudgetCountyPractices(_budgetFilter, globalState);
      setBudgetCountyPractices(_allCountyPractices);
    } catch (err) {
      setBudgetCountyPractices([]);
      handleError(err);
    }
  };

  useEffect(() => {
    loadBudgetCountyPractices();
  }, [globalState?.producer_token, producer_token, currentYear]);

  // ---

  const loadUniqueCropPractices = async () => {
    try {
      // Get unique:
      const _uniqueCropPractices = getUniqueCropPractices(budgetCountyPractices);

      const _loadedCropPractices = [];
      for (const cp of _uniqueCropPractices) {
        const _compareBudget = cp?.children?.length && cp.children[0];
        // const futures = await selectFuturesByBudget({
        //     year: currentYear,
        //     ..._compareBudget
        // });
        // if (futures?.length) {
        //     cp.futures = futures;
        // } else {
        //     cp.futures = null;
        // }

        cp.unit_of_measure_abbreviation =
          (await getUnitOfMeasureAbbreviation({
            reinsurance_year: currentYear,
            year: currentYear,
            ..._compareBudget,
          })) || "";

        _loadedCropPractices.push(cp);
      }

      setUniqueCropPractices(_loadedCropPractices);
    } catch (err) {
      setUniqueCropPractices([]);
      handleError(err);
    }
  };

  const [uniqueCropPractices, setUniqueCropPractices] = useState(null);
  useEffect(() => {
    if (budgetCountyPractices != null) {
      loadUniqueCropPractices();
    }
  }, [budgetCountyPractices]);

  //useCallback to cache getUnitOfMeasureAbbreviation
  const _getUnitOfMeasureAbbreviation = useCallback(
    (countyPractice) => {
      // Find in uniqueCropPractices:
      const _uniqueCropPractice = uniqueCropPractices?.find((cp) => cp.commodity_name == countyPractice.commodity_name);
      if (_uniqueCropPractice) {
        return _uniqueCropPractice.unit_of_measure_abbreviation;
      } else {
        return "";
      }
    },
    [uniqueCropPractices]
  );

  // Memoize practice calculations to prevent recalculation on every render
  const practiceCalculations = useMemo(() => {
    if (!reportData?.length || !budgetCountyPractices?.length) return {};

    const calculations = {};

    reportData.forEach((currentCountyPractice) => {
      const commodityName = currentCountyPractice.commodity_name;
      const practiceGroups = _.groupBy(
        budgetCountyPractices?.filter((bcp) => bcp.commodity_name === commodityName),
        "practice_name"
      );

      const hasIrrigated = Object.keys(practiceGroups).includes("Irrigated");
      const hasNonIrrigated = Object.keys(practiceGroups).includes("Non-Irrigated");

      // Calculate acres display
      const acresDisplay = Object.entries(practiceGroups)
        .map(([name, practices]) => {
          const result = { name, acres: _.sumBy(practices, "reported_acreage") };

          if (name === "Irrigated" && !hasNonIrrigated) {
            return [result, { name: "Non-Irrigated", acres: 0 }];
          }
          if (name === "Non-Irrigated" && !hasIrrigated) {
            return [{ name: "Irrigated", acres: 0 }, result];
          }
          return result;
        })
        .flat();

      // Calculate yield display
      const yieldDisplay = Object.entries(practiceGroups)
        .map(([name, practices]) => {
          const totalAcres = _.sumBy(practices, "reported_acreage");
          const weightedYield = _.sumBy(practices, (p) => p.yield * p.reported_acreage) / totalAcres;
          const result = { name, yield: weightedYield || 0 };

          if (name === "Irrigated" && !hasNonIrrigated) {
            return [result, { name: "Non-Irrigated", yield: 0 }];
          }
          if (name === "Non-Irrigated" && !hasIrrigated) {
            return [{ name: "Irrigated", yield: 0 }, result];
          }
          return result;
        })
        .flat();

      calculations[commodityName] = {
        acresDisplay,
        yieldDisplay,
        unitAbbreviation: _getUnitOfMeasureAbbreviation(currentCountyPractice)?.toLowerCase(),
      };
    });

    return calculations;
  }, [reportData, budgetCountyPractices, _getUnitOfMeasureAbbreviation]);

  // Memoize profit calculations to prevent recalculation on every render
  const profitCalculations = useMemo(() => {
    if (!reportData?.length) return {};

    const calculations = {};

    reportData.forEach((currentCountyPractice) => {
      const commodityName = currentCountyPractice.commodity_name;
      const totalProduction = currentCountyPractice?.production || 1;
      const totalAcres = calculateTotalAcresForCrop(commodityName);

      const marketingTotal = calculateFinalMarketingTotal(
        calculateMarketingTotal(commodityName),
        getTransactionHistoryTotal_Revenue(commodityName)
      );
      const cropInsurancePayments = getCropInsurancePaymentForCrop(commodityName);
      const fsaPayments = getArcPlcPaymentForCrop(commodityName) || 0;
      const inputCosts = calculateTotalCostForCrop(commodityName);

      const totalProfit = marketingTotal + cropInsurancePayments + fsaPayments - inputCosts;

      calculations[commodityName] = {
        profitPerUnit: totalProfit / totalProduction,
        profitPerAcre: totalProfit / totalAcres,
        totalProfit: totalProfit,
      };
    });

    return calculations;
  }, [
    reportData,
    calculateTotalAcresForCrop,
    calculateFinalMarketingTotal,
    calculateMarketingTotal,
    getTransactionHistoryTotal_Revenue,
    getCropInsurancePaymentForCrop,
    getArcPlcPaymentForCrop,
    calculateTotalCostForCrop,
  ]);

  // --- --- ---

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    commodity_name: default_commodity_name ? [default_commodity_name] : null,
    filter_agent_token: globalState?.filter_agent_token || [globalState?.agent_token] || [],
    filter_is_posted: false,
  });

  const filterHandleChangeState = async (event, value) => {
    if (value == null && event.target.value != null) {
      value = event.target.value;
    }

    dispatchFilter({
      type: "update",
      payload: { key: event.target.name, value: value },
    });
  };

  useEffect(() => {
    if (currentYear) {
      // Clear so soft-nav to another producer doesn't keep the prior header/name
      // (reportData[0] → formatProducerName) while budget acres already refreshed.
      setPositionSummaryData(null);
      setSalesByCategory(null);
      _loadPositionSummaryData();
      _loadSalesByCategory();
      _loadTransactionHistory();
      _loadSales();
    }
  }, [currentYear, filter, producer_token, globalState?.producer_token]);

  // ---

  const [editingSale, setEditingSale] = useState(null);

  const handleClickNewSale = async () => {
    setEditingSale({
      producer_token: globalState.producer_token,
      crop_year: currentYear,
      trade_date: new Date(),
    });
  };

  const [editingFuturesOptions, setEditingFuturesOptions] = useState(null);

  const handleClickNewFuture = async () => {
    setEditingFuturesOptions({
      producer_token: globalState.producer_token,
      crop_year: currentYear,
      trade_date: new Date(),
    });
  };

  const isValid = () => {
    return true;
  };

  const updateSale = async (sale) => {
    try {
      const api = new ToolkitApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });

      if (!sale.producer_token) {
        sale.producer_token = producer_token || globalState.producer_token;
      }
      if (!sale.crop_year) {
        sale.crop_year = currentYear;
      }

      await api.callPost("marketing/Sale", sale, globalState);
    } catch (err) {
      handleError(err);
    }
  };

  const futuresOptionsUpdate = async (futuresOptions) => {
    try {
      const api = new ToolkitApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });
      await api.callPost("broker/MarketTransactionHistory", futuresOptions, globalState);
    } catch (err) {
      handleError(err);
    }
  };
  // --- --- ---

  const [editingCropProfile, setEditingCropProfile] = useState(null);

  useEffect(() => {
    loadCropProfiles();
  }, [producer_token, globalState?.producer_token, globalState?.agent_token]);

  const loadCropProfiles = async () => {
    setIsLoadingCropProfiles(true);
    try {
      const _cropProfiles = await UsersApiHelper.users_search(
        "CropProfile",
        { producer_token, is_active: true },
        globalState
      );

      setCropProfiles(_cropProfiles);
      setIsLoadingCropProfiles(false);

      return _cropProfiles;
    } catch (error) {
      enqueueSnackbar(`Failed to fetch crop profiles: ${error.message}`, { variant: "error" });
      setCropProfiles(null);
      setIsLoadingCropProfiles(false);
      return [];
    }
  };

  const fetchCropProfilesForRow = useCallback(() => {
    try {
      const _formattedCropProfiles = cropProfiles.map((cp) => {
        const cropProfileDescription = formatCropProfile(cp);
        return cropProfileDescription;
      });

      return _formattedCropProfiles;
    } catch (error) {
      enqueueSnackbar(`Failed to fetch crop profiles: ${error.message}`, { variant: "error" });
      return [];
    }
  }, [cropProfiles]);

  const handleChange_CropProfile = async (_newCropProfile) => {
    try {
      const api = new ToolkitApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });
      await api.callPost("marketing/CropProfile", _newCropProfile, globalState);
    } catch (err) {
      handleError(err);
    }
  };

  // ---

  const handleDispatchRowChange = useCallback(
    async (id, key, value) => {
      const _transactionHistory = [...transactionHistory];
      const _index = _transactionHistory.findIndex((hist) => hist.id == id);
      if (_index > -1) {
        if (key == "crop_profile" && value) {
          // Lookup crop profile from producerCropProfiles[producer_token]
          if (cropProfiles?.length) {
            const _cropProfile = cropProfiles?.find((cp) => value == formatCropProfile(cp));
            if (_cropProfile) {
              confirmDialogActions.confirm({
                title: "Change Crop Profile",
                prompt: "Do you wish to change the Crop Profile for this record?",
                promptDetail: null,
                callback: () => {
                  const updatedItem = {
                    ..._transactionHistory[_index],
                    [key]: value,
                    crop_profile_commodity_name: _cropProfile?.commodity_name,
                    crop_profile_year: _cropProfile?.year,
                    crop_profile_name: _cropProfile?.name,
                  };
                  _transactionHistory[_index] = updatedItem;

                  updateTransactionHistory(updatedItem); //Do not wait.
                  setTransactionHistory(_transactionHistory);
                },
              });
            }
          }
        } else {
          _transactionHistory[_index][key] = value;
          updateTransactionHistory(_transactionHistory[_index]); //Do not wait.
          setTransactionHistory(_transactionHistory);
        }
      }
    },
    [transactionHistory, setTransactionHistory, cropProfiles]
  );

  const handleDeleteTransactionOnClick = (row) => {
    const prompt = "Are you sure you want to delete this entry?";
    var promptDetail = null;

    if (row?.quantity_open !== row?.quantity) {
      promptDetail = "Warning: Deleting this entry will delete the offsetting trade.";
    }

    confirmDialogActions.confirm({
      title: "Delete this entry?",
      prompt: prompt,
      promptDetail: promptDetail,
      callback: () => {
        handleDispatchRowChange(row.id, "is_active", false);
      },
    });
  };

  // ---

  const handleToggleNoteVisibility = (id) => {
    const _transactionHistory = [...transactionHistory];
    const _index = _transactionHistory.findIndex((hist) => hist.id == id);

    _transactionHistory[_index].showNote = !_transactionHistory[_index].showNote;
    setTransactionHistory(_transactionHistory);
  };

  const handleNoteChange = (e, row) => {
    const { value } = e.target;
    if (value) {
      row.note = value;

      const _transactionHistory = [...transactionHistory];
      const _index = _transactionHistory.findIndex((hist) => hist.id == row.id);

      _transactionHistory[_index].note = value;
      setTransactionHistory(_transactionHistory);
      updateTransactionHistory(row);
    }
  };

  // --- --- ---

  const [selectedOTCRow, setSelectedOTCRow] = useState(null);

  const calculateChartData = useCallback(
    (currentCountyPractice) => {
      let totalQty = 0;

      if (currentCountyPractice?.salesByCategory?.length) {
        [SALE_CATEGORY_CASH, SALE_CATEGORY_HTA, SALE_CATEGORY_BASIS, SALE_CATEGORY_NO_PRICE].forEach((category) => {
          const ps = currentCountyPractice?.salesByCategory?.find((s) => s.category == category);
          if (ps) {
            totalQty += ps?.amount;
          }
        });
      }

      return [
        {
          category: "1 Cash",
          id: 1,
          dataValueY_mean:
            currentCountyPractice?.salesByCategory?.find((s) => s.category == SALE_CATEGORY_CASH)?.amount || 0,
          stack: 1,
        },
        {
          category: "2 HTA",
          id: 2,
          dataValueY_mean:
            currentCountyPractice?.salesByCategory?.find((s) => s.category == SALE_CATEGORY_HTA)?.amount || 0,
          stack: 1,
        },
        {
          category: "3 Futures / Options",
          id: 3,
          dataValueY_mean: calculateFuturesOptionsHedgedTotal(currentCountyPractice?.commodity_name) || 0,
          stack: 1,
        },
        {
          category: "4 Basis",
          id: 4,
          dataValueY_mean:
            currentCountyPractice?.salesByCategory?.find((s) => s.category == SALE_CATEGORY_BASIS)?.amount || 0,
          stack: 1,
        },
        {
          category: "5 No Price Established",
          id: 5,
          dataValueY_mean:
            currentCountyPractice?.salesByCategory?.find((s) => s.category == SALE_CATEGORY_NO_PRICE)?.amount || 0,
          stack: 1,
        },
        {
          category: "6 Unprotected",
          id: 6,
          dataValueY_mean: Math.max(
            0,
            currentCountyPractice?.production -
              totalQty -
              calculateFuturesOptionsHedgedTotal(currentCountyPractice?.commodity_name) || 0
          ),
          stack: 1,
        },
      ];
    },
    [calculateFuturesOptionsHedgedTotal, currentYear, filter, positionSummaryData, salesByCategory, transactionHistory]
  );

  useEffect(() => {
    addAttachments([
      {
        name: "Position Summary" + (reportData?.length ? " - " + formatProducerName(reportData[0] || "") : ""),
        content: reportData,
      },
    ]);
  }, [reportData]);

  return (
    <Box sx={{ minHeight: "800px" }}>
      <WorkspaceHeader
        title="Position Summary"
        subtitle={reportData?.length ? formatProducerName(reportData[0] || "") : <>&nbsp;</>}
        category={{
          title: "Marketing",
          icon: <MarketingIcon />,
        }}
      />

      <Grid container className="no-print">
        <Grid item xs={12}>
          <Stack
            className="headerWithMenuButton marketing-position"
            sx={{
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <DataFormSelect
              value={filter.commodity_name || []}
              name="commodity_name"
              isRequired={false}
              onChange={filterHandleChangeState}
              objectNameLabel="Commodity"
              itemGetKey={(item) => item.commodity_name}
              itemGetLabel={(item) => item.commodity_name}
              doFetch={currentYear !== null}
              isLoading={false}
              multiple={true}
              limitTags={isSmall ? 2 : 3}
              autoSelect={false}
              customDataLoader={() => RmaApiHelper.searchCommodity({}, {})}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "start",
                gap: "1rem",
                paddingTop: { md: "1.75rem" },
                marginBottom: "1rem !important",
              }}
            >
              <PopupState variant="popover" popupId="position-summary-year-menu">
                {(popupState) => (
                  <>
                    <Button
                      variant="outlined"
                      data-testid="position-summary-year-selector"
                      {...bindTrigger(popupState)}
                    >
                      {currentYear} <ArrowDropDownOutlined />
                    </Button>
                    <Menu {...bindMenu(popupState)}>
                      {getBudgetYears()?.map((year, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => {
                            setCurrentYear(year);
                            popupState.close();
                          }}
                        >
                          {year}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </PopupState>

              {isLoadingCurrent && <CircularProgress />}
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {(() => {
        return reportData?.length;
      })() ? (
        reportData?.map((currentCountyPractice, idx) => (
          <div
            key={idx + "-" + currentCountyPractice.commodity_name}
            className={idx > 0 ? "pageBreakBefore" : ""}
            data-testid={currentCountyPractice.commodity_name}
          >
            <Typography variant="h6" sx={{ pb: 1 }}>
              {currentCountyPractice.commodity_name}
            </Typography>
            <Accordion
              className="riskSummaryAccordion pageBreakAvoid"
              sx={{ mt: 1 }}
              expanded={accordionExpanded.production}
              onChange={(_, expanded) => setAccordionSection("production", expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>Production Summary</AccordionSummary>
              <AccordionDetails>
                <ProductionSummaryTable
                  currentCountyPractice={currentCountyPractice}
                  practiceCalculations={practiceCalculations}
                  _getUnitOfMeasureAbbreviation={_getUnitOfMeasureAbbreviation}
                  cropProfileBasis={
                    Number(cropProfiles?.find(
                      (cp) =>
                        cp.commodity_name?.trim().toLowerCase() ===
                          currentCountyPractice?.commodity_name?.trim().toLowerCase() &&
                        String(cp.year) === String(currentYear)
                    )?.basis) || 0
                  }
                />
              </AccordionDetails>
            </Accordion>

            <Accordion
              className="riskSummaryAccordion pageBreakAvoid"
              sx={{ mt: 1 }}
              expanded={accordionExpanded.positionSummary}
              onChange={(_, expanded) => setAccordionSection("positionSummary", expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>Position Summary</AccordionSummary>
              <AccordionDetails>
                <FuturesOptionsPnLCard
                  currentCountyPractice={currentCountyPractice}
                  getTransactionHistoryRowsByCommodity={getTransactionHistoryRowsByCommodity}
                  getTransactionHistoryRowsByMatch={getTransactionHistoryRowsByMatch}
                />
              </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 2, mb: 1 }}>
              {isLoading || isLoadingSalesByCategory || isLoadingTransactionHistory || isLoadingCurrent ? (
                <Box className="position-summary-chart">
                  <ChartBase
                    data={[]}
                    scaleColors={[]}
                    size="small"
                    thresholdValue={0}
                    allowedChartTypes={[CHART_TYPE_HORIZONTAL_STACKED_BAR]}
                    maxHeight={100}
                    renderer="svg"
                  />
                </Box>
              ) : (
                <Box className="position-summary-chart">
                  <ChartBase
                    data={calculateChartData(currentCountyPractice)}
                    scaleColors={[
                      theme.palette.colors.textVibrantGreen,
                      theme.palette.colors.textDarkGreen,
                      theme.palette.colors.textGraySlate,
                      theme.palette.colors.textWarningYellow,
                      theme.palette.colors.textOrangeAccent,
                      theme.palette.colors.textDangerRed,
                    ]}
                    size="small"
                    thresholdValue={currentCountyPractice?.production}
                    allowedChartTypes={[CHART_TYPE_HORIZONTAL_STACKED_BAR]}
                    maxHeight={100}
                    renderer="svg"
                  />
                </Box>
              )}
            </Box>

            <Accordion
              className="riskSummaryAccordion pageBreakAvoid"
              sx={{ mt: 1 }}
              expanded={accordionExpanded.cashSummary}
              onChange={(_, expanded) => setAccordionSection("cashSummary", expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                  <Typography>Cash Summary</Typography>
                  {isMobileTable && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const commodityName = currentCountyPractice?.commodity_name;
                        setCashSummaryAllExpanded((prev) => ({
                          ...prev,
                          [commodityName]: !prev[commodityName],
                        }));
                      }}
                      aria-label="Expand/Collapse all"
                    >
                      {cashSummaryAllExpanded[currentCountyPractice?.commodity_name] ? (
                        <UnfoldLessIcon fontSize="small" />
                      ) : (
                        <UnfoldMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <CashSummaryTable
                  currentCountyPractice={currentCountyPractice}
                  cropProfileBasis={
                    Number(cropProfiles?.find(
                      (cp) =>
                        cp.commodity_name?.trim().toLowerCase() ===
                          currentCountyPractice?.commodity_name?.trim().toLowerCase() &&
                        String(cp.year) === String(currentYear)
                    )?.basis) || 0
                  }
                  calculateFuturesOptionsHedgedTotal={calculateFuturesOptionsHedgedTotal}
                  calculateOTCHedgedTotal={calculateOTCHedgedTotal}
                  calculateOTCAccumulatedTotal={calculateOTCAccumulatedTotal}
                  getTotalRevenueIncludingOTCAccumulated={getTotalRevenueIncludingOTCAccumulated}
                  getTransactionHistoryOTCHedgedTotal_Revenue={getTransactionHistoryOTCHedgedTotal_Revenue}
                  getOTCAccumulatedAveragePrice={getOTCAccumulatedAveragePrice}
                  getOTCAccumulatedMarketValue={getOTCAccumulatedMarketValue}
                  hasOTC={hasOTC}
                  calculateTotalAcresForCrop={calculateTotalAcresForCrop}
                  calculateFinalMarketingTotal={calculateFinalMarketingTotal}
                  calculateMarketingTotal={calculateMarketingTotal}
                  getCropInsurancePaymentForCrop={getCropInsurancePaymentForCrop}
                  getArcPlcPaymentForCrop={getArcPlcPaymentForCrop}
                  calculateTotalCostForCrop={calculateTotalCostForCrop}
                  profitCalculations={profitCalculations}
                  isLoadingRevenueData={isLoadingRevenueData}
                  _getUnitOfMeasureAbbreviation={_getUnitOfMeasureAbbreviation}
                  allExpanded={cashSummaryAllExpanded[currentCountyPractice?.commodity_name] || false}
                  onToggleAll={(value) => {
                    const commodityName = currentCountyPractice?.commodity_name;
                    setCashSummaryAllExpanded((prev) => ({
                      ...prev,
                      [commodityName]: value,
                    }));
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              className="riskSummaryAccordion pageBreakBefore"
              sx={{ mt: 1 }}
              expanded={accordionExpanded.cashSales}
              onChange={(_, expanded) => setAccordionSection("cashSales", expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>Cash Sales</AccordionSummary>
              <AccordionDetails>
                <div
                  className="position-summary-report-cash-sales"
                  data-testid={`cash-sales-grid-${currentCountyPractice?.commodity_name?.toLowerCase()}`}
                >
                  <PrintableTilleyDataGrid
                    hideColumns={["commodity_name", "actions"]}
                    columnVisibilityModel={{ commodity_name: false }} // rows are already filtered to this commodity
                    rows={getSalesRowsByCommodity(currentCountyPractice?.commodity_name) || []}
                    columns={sales_columns}
                    emptyMessage="No sales data available"
                    pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
                    defaultPageSize={isMobile ? 5 : 10}
                    gridKey="position-summary-report-cash-sales"
                    getRowId={(row) => row.id}
                    isLoading={isLoadingCurrent}
                    sx={{ minHeight: "200px" }}
                    isScrollToTopOnPageChange={true}
                  />
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion
              className="riskSummaryAccordion pageBreakBefore"
              data-testid="futures-options-accordion"
              sx={{ mt: 1 }}
              expanded={accordionExpanded.positions}
              onChange={(_, expanded) => setAccordionSection("positions", expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "space-between",
                    pr: 1,
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Positions
                    <Box sx={{ pl: 2 }}>{isLoadingTransactionHistory && <CircularProgress size={20} />}</Box>
                  </Box>
                  <Box
                    className="no-print"
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                  >
                    <FuturesOptionsExportButton
                      currentCountyPractice={currentCountyPractice}
                      currentYear={currentYear}
                      getTransactionHistoryRowsByCommodity={getTransactionHistoryRowsByCommodity}
                      getTransactionHistoryRowsByMatch={getTransactionHistoryRowsByMatch}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <FuturesOptionsPanels
                  currentCountyPractice={currentCountyPractice}
                  currentYear={currentYear}
                  getTransactionHistoryRowsByCommodity={getTransactionHistoryRowsByCommodity}
                  getTransactionHistoryRowsByMatch={getTransactionHistoryRowsByMatch}
                  handleToggleNoteVisibility={handleToggleNoteVisibility}
                  handleDispatchRowChange={handleDispatchRowChange}
                  handleNoteChange={handleNoteChange}
                  handleDeleteTransactionOnClick={handleDeleteTransactionOnClick}
                  fetchCropProfilesForRow={fetchCropProfilesForRow}
                  formatDefaultCropProfile={formatDefaultCropProfile}
                  isLoadingCropProfiles={isLoadingCropProfiles}
                  setSelectedOTCRow={setSelectedOTCRow}
                />
              </AccordionDetails>
            </Accordion>

            <Divider />
          </div>
        ))
      ) : (
        <EmptyCard text="No Position Summary Defined" secondaryText="Get started by adding a Sale or Futures position" />
      )}

      <WorkspaceActionBar
        content={
          <>
            <Button className="add-sale" variant="contained" onClick={handleClickNewSale} disabled={!isValid()}>
              <AddCircleOutline sx={{ mr: 1 }} />
              Add Sale
            </Button>
            <Button
              className="add-futures"
              variant="contained"
              color="secondary"
              sx={{ ml: 2 }}
              onClick={handleClickNewFuture}
            >
              <AddCircleOutline sx={{ mr: 1 }} />
              Add Futures
            </Button>
          </>
        }
      />

      <Dialog open={Boolean(editingSale != null)}>
        <SalesEditDialog
          producer_token={producer_token || globalState?.producer_token} //Override producer_token
          onChange={updateSale}
          onClose={() => {
            setEditingSale(null);
            setIsLoading(true);
            _loadSales();
            _loadSalesByCategory();
          }}
          sale={editingSale}
          crop_year={currentYear}
        />
      </Dialog>

      <Dialog open={Boolean(editingFuturesOptions != null)}>
        <FuturesOptionsEditDialog
          producer_token={producer_token || globalState?.producer_token} //Override producer_token
          crop_year={currentYear}
          onChange={async (futuresOptions) => {
            await futuresOptionsUpdate(futuresOptions);
            _loadTransactionHistory();
          }}
          onClose={async () => {
            setEditingFuturesOptions(null);
            _loadTransactionHistory();
          }}
          futureOptions={editingFuturesOptions}
        />
      </Dialog>

      <Dialog open={Boolean(editingCropProfile)}>
        <CropProfileEditDialog
          cropProfile={editingCropProfile}
          onClose={() => setEditingCropProfile(null)}
          onChange={handleChange_CropProfile}
        />
      </Dialog>

      <OTCDescriptionDialog
        open={Boolean(selectedOTCRow)}
        onClose={() => setSelectedOTCRow(null)}
        row={selectedOTCRow}
        producer_token={producer_token || globalState?.producer_token}
      />

      {confirmDialogComponent}
    </Box>
  );
}
